import { zodResolver } from '@hookform/resolvers/zod';
import { type FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import z from 'zod';

import { Info } from '@/components/icons/barrel';
import { Button } from '@/components/ui/button';
import Copyable from '@/components/ui/copyable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormField } from '@/components/ui/form';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePrivyAccount } from '@/hooks';
import useRegisterUser from '@/lib/api/register-user';
import { useAccountStore } from '@/store/use-account-store';
import { ErrorCode, ErrorMsgs } from '@/types';
import { truncateAddress } from '@/utils/format';

import { Checkbox } from '../../ui';
import { Label } from '../../ui/label';

interface IRegisterDialogProps {
  isForPrimary?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const formSchema = z.object({
  rememberMe: z.boolean(),
});

type FormSchema = z.infer<typeof formSchema>;

const RegisterDialog: FC<IRegisterDialogProps> = ({
  isForPrimary,
  open,
  setOpen,
}) => {
  const {
    email,
    address: primaryAddress,
    hasEmail,
    isConnected,
    disconnect,
  } = usePrivyAccount();
  const { t } = useTranslation();

  const storeAddress = useAccountStore((state) => state.address);
  const address = isForPrimary ? primaryAddress : storeAddress;

  const { mutateAsync: registerUser, isPending: isRegistering } =
    useRegisterUser();

  const onSubmit = async ({ rememberMe }: FormSchema) => {
    if (address) {
      try {
        await registerUser({
          address,
          rememberMe,
        });
        setOpen(false);
      } catch (error) {
        console.error(error);

        const topLevelError = (error as any)?.error;
        if (topLevelError) {
          toast.error(`Registration failed - ${topLevelError}`);
          return;
        }

        // Safely destructure nested error code if it exists
        const code: ErrorCode = (error as any)?.response?.data?.detail?.code;

        if (code && code in ErrorMsgs) {
          toast.error(`Registration failed - ${ErrorMsgs[code]}`);
        } else {
          toast.error(
            'Registration failed. Please try again or contact support.',
          );
        }
      } finally {
        localStorage.removeItem('ref-code');
      }
    } else {
      toast.error('Please connect your wallet first.');
    }
  };

  const { pathname } = useLocation();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rememberMe: true,
    },
  });

  if (pathname === '/privacy' || pathname === '/terms' || !isConnected) {
    return;
  }

  return (
    <Dialog open={open}>
      <DialogContent
        hideCloseButton
        className="gap-0 border border-border bg-background p-0 sm:max-w-md sm:rounded-none"
      >
        <DialogHeader className="px-5 pt-6">
          <DialogTitle className="flex items-center gap-4 text-xl text-foreground">
            {t('registerDialog.signIn')}
            <div className="flex items-center text-lg font-normal text-vestgrey-100">
              (
              {hasEmail
                ? email ?? truncateAddress(address ?? '', 6)
                : truncateAddress(address ?? '', 6)}
              {!hasEmail && <Copyable>{address ?? ''}</Copyable>})
            </div>
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6 px-5 pb-10 pt-6">
              <p className="leading-relaxed text-vestgrey-100">
                {hasEmail
                  ? t('registerDialog.embeddedMessage')
                  : t('registerDialog.walletMessage')}{' '}
                <a
                  className="underline"
                  href={'/terms'}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('registerDialog.termsOfService')}
                </a>{' '}
                and{' '}
                <a
                  className="underline"
                  href={'/privacy'}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('registerDialog.privacyPolicy')}
                </a>
                .
              </p>
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Checkbox
                      className="size-5"
                      id="remember-me"
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                    <Label
                      htmlFor="remember-me"
                      className="text-base text-vestgrey-100"
                    >
                      {t('registerDialog.rememberMe')}
                    </Label>
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger className="text-vestgrey-100">
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent>
                          {t('registerDialog.rememberMeTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              />
              {/* <FormField
                control={form.control}
                name="alphaConsent"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex gap-3">
                      <Checkbox
                        id="alpha-consent"
                        className="mt-1 size-5"
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked as boolean)
                        }
                      />
                      <FormLabel
                        htmlFor="alpha-consent"
                        className="cursor-pointer leading-snug"
                      >
                        {t('registerDialog.alphaConsent')}
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
            </div>
            <DialogFooter className="flex-col sm:flex-col sm:gap-0 sm:space-x-0">
              <Button
                className="h-14 w-full rounded-none font-mono text-lg font-normal tracking-wider"
                disabled={isRegistering}
              >
                {isRegistering
                  ? t('registerDialog.confirming')
                  : hasEmail
                    ? t('registerDialog.signInButton')
                    : t('registerDialog.signMessageButton')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  disconnect({
                    // reload: true,
                    onSuccess: () => setOpen(false),
                  })
                }
                className="m-0 h-14 w-full rounded-none font-mono text-lg font-normal tracking-wider text-foreground"
                disabled={isRegistering}
              >
                {hasEmail
                  ? t('registerDialog.logOut')
                  : t('registerDialog.disconnect')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterDialog;
